import React, { useState, useEffect, useLayoutEffect, useRef } from 'react';
import useInfiniteScroll, { FEED_PAGE_SIZE } from '../../hooks/useInfiniteScroll';
import { usePagedFeed, useEnsureFeedCount, feedMayHaveMore } from '../../hooks/usePagedFeed';
import { T } from '../Translate';
import {
  Container,
  AppBar,
  Toolbar,
  CardMedia,
  Box,
  Typography,
  Button,
  Grid,
  IconButton,
  TextField,
  MenuItem,
  Chip,
  Skeleton,
  Drawer,
  Paper,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { Search as SearchIcon } from '../../icons';
import { Close as CloseIcon } from '../../icons';
import { CalendarMonth as CalendarMonthIcon } from '../../icons';
import { Speed as SpeedIcon } from '../../icons';
import { LocationOn as LocationOnIcon } from '../../icons';
import { useNavigate } from 'react-router-dom';
import { DirectionsCar, DirectionsCarFilled, TimeToLeave, Build } from '../../icons';
import logoSvg from '../../imgs/logo.png';
import logo1 from '../../imgs/logo1.png';
import BottomToolbar from '../BottomToolbar';
import CustomToolbar from '../CustomToolbar';
import ListingImageSlideshow from '../../components/ListingImageSlideshow';
import ListingPost, { useListingSocial } from '../../components/ListingPost';
import useAuthorPhotos from '../useAuthorPhotos';
import { featuredFirst, isFeatured } from '../../utils/featuredListings';
import { brandColors, barColors, themeMode } from '../../theme';
import { pageContentSx, DESKTOP_MEDIA_QUERY, PAGE_CONTENT_MAX_WIDTH, FILTER_PANEL_BOTTOM_GAP, filterPanelScrollSx, listingCardSx, listingBodySx, listingMediaSx, listingContentSx, feedPostHeaderSx, listingImageHeight, listingImageRadius } from '../../pageLayout';
import { useDesktopSidebar } from '../../desktopSidebarContext';
import { useGeolocation } from '../../hooks/useGeolocation';
import { getCityFromCoordinates } from '../../utils/geo';
import SheetDragHandle from '../../components/SheetDragHandle';

import SearchClearAdornment from "../../components/SearchClearAdornment";
// Texto para los chips: un dato con forma rara (p. ej. una marca guardada como
// {name, "@type"} por una importación) no tiene que romper toda la página.
const asText = (value) => {
  if (value == null || value === '') return '';
  if (typeof value === 'object') return typeof value.name === 'string' ? value.name : '';
  return String(value);
};

const mapCarDoc = (doc) => {
  const data = doc.data();
  return {
    id: doc.id,
    ...data,
    title: asText(data.title),
    marca: asText(data.marca),
    modelo: asText(data.modelo),
    año: asText(data.año),
    km: asText(data.km),
    city: asText(data.city),
    imageUrls: Array.isArray(data.images) ? data.images : [data.images].filter(Boolean),
  };
};

const logo5 = `${process.env.PUBLIC_URL}/logo5.png`;

const CAR_CONDITIONS = [
  {
    id: 'Nuevo',
    label: <T>Nuevo</T>,
    color: '#4a90e2',
    gradient: 'linear-gradient(135deg, #4a90e2 0%, #357abd 100%)',
    icon: DirectionsCar,
    description: <T>Vehículos 0km directo de concesionaria</T>
  },
  {
    id: 'Usado',
    label: <T>Usado</T>,
    color: '#48bb78',
    gradient: 'linear-gradient(135deg, #48bb78 0%, #38a169 100%)',
    icon: TimeToLeave,
    description: <T>Vehículos usados en excelente estado</T>
  },
  {
    id: 'Reparar',
    label: <T>Para reparar</T>,
    color: '#ed8936',
    gradient: 'linear-gradient(135deg, #ed8936 0%, #dd6b20 100%)',
    icon: Build,
    description: <T>Vehículos que necesitan reparación</T>
  }
];

const CAR_BRANDS = [
  { value: 'Acura', label: <T>Acura</T> },
  { value: 'Alfa Romeo', label: <T>Alfa Romeo</T> },
  { value: 'Aston Martin', label: <T>Aston Martin</T> },
  { value: 'Audi', label: <T>Audi</T> },
  { value: 'Bentley', label: <T>Bentley</T> },
  { value: 'BMW', label: <T>BMW</T> },
  { value: 'Bugatti', label: <T>Bugatti</T> },
  { value: 'Buick', label: <T>Buick</T> },
  { value: 'BYD', label: <T>BYD</T> },
  { value: 'Cadillac', label: <T>Cadillac</T> },
  { value: 'Changan', label: <T>Changan</T> },
  { value: 'Chery', label: <T>Chery</T> },
  { value: 'Chevrolet', label: <T>Chevrolet</T> },
  { value: 'Chrysler', label: <T>Chrysler</T> },
  { value: 'Citroën', label: <T>Citroën</T> },
  { value: 'Dodge', label: <T>Dodge</T> },
  { value: 'DS', label: <T>DS</T> },
  { value: 'Ferrari', label: <T>Ferrari</T> },
  { value: 'Fiat', label: <T>Fiat</T> },
  { value: 'Ford', label: <T>Ford</T> },
  { value: 'Genesis', label: <T>Genesis</T> },
  { value: 'GMC', label: <T>GMC</T> },
  { value: 'Great Wall', label: <T>Great Wall</T> },
  { value: 'Honda', label: <T>Honda</T> },
  { value: 'Hyundai', label: <T>Hyundai</T> },
  { value: 'Infiniti', label: <T>Infiniti</T> },
  { value: 'Isuzu', label: <T>Isuzu</T> },
  { value: 'Jaguar', label: <T>Jaguar</T> },
  { value: 'Jeep', label: <T>Jeep</T> },
  { value: 'Kia', label: <T>Kia</T> },
  { value: 'Lada', label: <T>Lada</T> },
  { value: 'Lamborghini', label: <T>Lamborghini</T> },
  { value: 'Land Rover', label: <T>Land Rover</T> },
  { value: 'Lexus', label: <T>Lexus</T> },
  { value: 'Maserati', label: <T>Maserati</T> },
  { value: 'Mazda', label: <T>Mazda</T> },
  { value: 'McLaren', label: <T>McLaren</T> },
  { value: 'Mercedes Benz', label: <T>Mercedes Benz</T> },
  { value: 'Mini', label: <T>Mini</T> },
  { value: 'Mitsubishi', label: <T>Mitsubishi</T> },
  { value: 'Nissan', label: <T>Nissan</T> },
  { value: 'Opel', label: <T>Opel</T> },
  { value: 'Peugeot', label: <T>Peugeot</T> },
  { value: 'Porsche', label: <T>Porsche</T> },
  { value: 'RAM', label: <T>RAM</T> },
  { value: 'Renault', label: <T>Renault</T> },
  { value: 'Rolls-Royce', label: <T>Rolls-Royce</T> },
  { value: 'Saab', label: <T>Saab</T> },
  { value: 'Seat', label: <T>Seat</T> },
  { value: 'Škoda', label: <T>Škoda</T> },
  { value: 'SsangYong', label: <T>SsangYong</T> },
  { value: 'Subaru', label: <T>Subaru</T> },
  { value: 'Suzuki', label: <T>Suzuki</T> },
  { value: 'Tesla', label: <T>Tesla</T> },
  { value: 'Toyota', label: <T>Toyota</T> },
  { value: 'Volkswagen', label: <T>Volkswagen</T> },
  { value: 'Volvo', label: <T>Volvo</T> },
];

const CarsPage = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isDesktop = useMediaQuery(DESKTOP_MEDIA_QUERY);
  const sidebarActive = useDesktopSidebar();
  // Con la sidebar el logo y las categorías viven en ella: el AppBar solo
  // queda (plano) para los íconos de buscar/filtros cuando no hay panel lateral.
  const showTopBar = !sidebarActive || !isDesktop;
  const navigate = useNavigate();

  // Autos de a tandas desde Firestore (ver usePagedFeed): antes se escuchaba
  // la colección entera. Los filtros se aplican sobre lo ya cargado.
  const carsFeed = usePagedFeed('cars', mapCarDoc);
  const social = useListingSocial('car');
  const authorPhotos = useAuthorPhotos(carsFeed.items.map((car) => car.uid));
  const cars = carsFeed.items;
  const [filteredCars, setFilteredCars] = useState([]);

  const [selectedCondition, setSelectedCondition] = useState('');
  const [selectedBrand, setSelectedBrand] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [filterCity, setFilterCity] = useState('');
  const [filterYearMin, setFilterYearMin] = useState('');
  const [filterYearMax, setFilterYearMax] = useState('');
  const [filterKmMax, setFilterKmMax] = useState('');
  const [filterPriceMin, setFilterPriceMin] = useState('');
  const [filterPriceMax, setFilterPriceMax] = useState('');

  // Mide el alto real del AppBar fijo en vez de adivinar un valor en px —
  // mismo criterio que home.js/events.js/editOffers.js.
  const appBarRef = useRef(null);
  const [appBarHeight, setAppBarHeight] = useState(96);
  useLayoutEffect(() => {
    const node = appBarRef.current;
    if (!node) {
      setAppBarHeight(0);
      return;
    }
    const updateHeight = () => setAppBarHeight(node.offsetHeight);
    updateHeight();
    const observer = new ResizeObserver(updateHeight);
    observer.observe(node);
    return () => observer.disconnect();
  }, [isSearchOpen, showTopBar]);

  const toggleSearch = () => setIsSearchOpen((open) => !open);

  // Ciudad del usuario (geolocalización) para priorizar en el orden por
  // defecto los autos publicados en su misma ciudad — no hay campo "country"
  // ni de ventas en los autos, así que no se puede replicar el ranking de
  // home.js tal cual (ver filterOffersByCountry/calculateOfferScore en
  // homeUtils.js); esto es lo más parecido con los datos que sí existen.
  const { position } = useGeolocation();
  const [userCity, setUserCity] = useState(null);
  useEffect(() => {
    if (!position) return;
    getCityFromCoordinates(position.latitude, position.longitude).then(setUserCity);
  }, [position]);

  useEffect(() => {
    if (!cars.length) return;

    const term = searchTerm.trim().toLowerCase();
    const city = filterCity.trim().toLowerCase();
    const yearMin = parseFloat(filterYearMin);
    const yearMax = parseFloat(filterYearMax);
    const kmMax = parseFloat(filterKmMax);
    const priceMin = parseFloat(filterPriceMin);
    const priceMax = parseFloat(filterPriceMax);

    const filtered = cars.filter((car) => {
      const carYear = parseFloat(car.año);
      const carKm = parseFloat(car.km);
      const carPrice = parseFloat(car.price);
      return (
        (!selectedCondition || (typeof car.condition === 'string' && typeof selectedCondition === 'string' && car.condition.toLowerCase() === selectedCondition.toLowerCase())) &&
        (!selectedBrand || (typeof car.marca === 'string' && typeof selectedBrand === 'string' && car.marca.toLowerCase() === selectedBrand.toLowerCase())) &&
        (!term || [car.title, car.marca, car.modelo, car.description].some(
          (field) => typeof field === 'string' && field.toLowerCase().includes(term)
        )) &&
        (!city || (typeof car.city === 'string' && car.city.toLowerCase().includes(city))) &&
        (isNaN(yearMin) || (!isNaN(carYear) && carYear >= yearMin)) &&
        (isNaN(yearMax) || (!isNaN(carYear) && carYear <= yearMax)) &&
        (isNaN(kmMax) || (!isNaN(carKm) && carKm <= kmMax)) &&
        (isNaN(priceMin) || (!isNaN(carPrice) && carPrice >= priceMin)) &&
        (isNaN(priceMax) || (!isNaN(carPrice) && carPrice <= priceMax))
      );
    });

    // Orden por defecto: autos de la ciudad del usuario primero (sort es
    // estable, así que dentro de cada grupo se conserva el orden por fecha
    // que ya trae la query de Firestore).
    if (userCity) {
      const matchesUserCity = (car) =>
        typeof car.city === 'string' && car.city.toLowerCase() === userCity.toLowerCase();
      filtered.sort((a, b) => Number(matchesUserCity(b)) - Number(matchesUserCity(a)));
    }

    // Las destacadas (pagaron "Destacar publicación") van siempre primero.
    setFilteredCars(featuredFirst(filtered));
  }, [cars, selectedCondition, selectedBrand, searchTerm, filterCity, filterYearMin, filterYearMax, filterKmMax, filterPriceMin, filterPriceMax, userCity]);

  // Muestra los vehículos de a 13 y suma otros 13 al acercarse al último.
  const { limit: carsLimit, sentinelRef: carsSentinelRef } = useInfiniteScroll(
    JSON.stringify([selectedCondition, selectedBrand, searchTerm, filterCity, filterYearMin, filterYearMax, filterKmMax, filterPriceMin, filterPriceMax]),
    FEED_PAGE_SIZE
  );
  // Si con los autos cargados no alcanzan los resultados, se piden más.
  useEnsureFeedCount(carsFeed, filteredCars.length, carsLimit);
  const loading = filteredCars.length === 0 && feedMayHaveMore(carsFeed);

  const hasActiveFilters = Boolean(
    searchTerm || selectedCondition || selectedBrand || filterCity ||
    filterYearMin || filterYearMax || filterKmMax || filterPriceMin || filterPriceMax
  );

  const handleClearFilters = () => {
    setSearchTerm('');
    setSelectedCondition('');
    setSelectedBrand('');
    setFilterCity('');
    setFilterYearMin('');
    setFilterYearMax('');
    setFilterKmMax('');
    setFilterPriceMin('');
    setFilterPriceMax('');
  };

  const styles = {
    header: {
      position: 'fixed',
      top: 0,
      width: '100%',
      bgcolor: barColors.background,
      zIndex: 1100
    },
    logoToolbar: {
      display: 'flex',
      justifyContent: 'center',
      padding: '8px 0',
    },
    // Mismo tamaño de logo y de Toolbar que home.js/events.js, para que el
    // AppBar tenga el mismo alto en todas las secciones.
    logo: {
      width: '130px',
      height: 'auto',
      aspectRatio: '847 / 231',
    },
  };

  const SkeletonCarCard = () => (
    <Box sx={{ ...listingCardSx, backgroundColor: brandColors.backgroundPaper }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, ...feedPostHeaderSx }}>
        <Skeleton variant="circular" width={40} height={40} />
        <Skeleton variant="text" width="40%" height={24} />
      </Box>
      <Box sx={listingBodySx}>
        <Box sx={listingMediaSx}>
          <Skeleton variant="rectangular" width="100%" sx={{ height: listingImageHeight, borderRadius: listingImageRadius }} />
        </Box>
        <Box sx={listingContentSx}>
          <Skeleton variant="text" width="70%" height={28} />
          <Skeleton variant="text" width="40%" height={28} sx={{ mb: 1 }} />
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mb: 2 }}>
            <Skeleton variant="rounded" width={70} height={24} />
            <Skeleton variant="rounded" width={70} height={24} />
            <Skeleton variant="rounded" width={70} height={24} />
          </Box>
          <Skeleton variant="rounded" width="100%" height={36} />
        </Box>
      </Box>
    </Box>
  );

  const filtersFields = (
    <>
      <TextField
        select
        fullWidth
        size="small"
        label={<T>Condición</T>}
        value={selectedCondition}
        onChange={(e) => setSelectedCondition(e.target.value)}
        sx={{ mb: 2 }}
      >
        <MenuItem value=""><T>Todas</T></MenuItem>
        {CAR_CONDITIONS.map((condition) => (
          <MenuItem key={condition.id} value={condition.id}>{condition.label}</MenuItem>
        ))}
      </TextField>
      <TextField
        select
        fullWidth
        size="small"
        label={<T>Marca</T>}
        value={selectedBrand}
        onChange={(e) => setSelectedBrand(e.target.value)}
        sx={{ mb: 2 }}
      >
        <MenuItem value=""><T>Todas</T></MenuItem>
        {CAR_BRANDS.map((brand) => (
          <MenuItem key={brand.value} value={brand.value}>{brand.label}</MenuItem>
        ))}
      </TextField>
      <TextField
        fullWidth
        size="small"
        label={<T>Ciudad</T>}
        value={filterCity}
        onChange={(e) => setFilterCity(e.target.value)}
        sx={{ mb: 2 }}
      />
      <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
        <TextField
          fullWidth
          size="small"
          type="number"
          label={<T>Año desde</T>}
          value={filterYearMin}
          onChange={(e) => setFilterYearMin(e.target.value)}
        />
        <TextField
          fullWidth
          size="small"
          type="number"
          label={<T>Año hasta</T>}
          value={filterYearMax}
          onChange={(e) => setFilterYearMax(e.target.value)}
        />
      </Box>
      <TextField
        fullWidth
        size="small"
        type="number"
        label={<T>Km máximo</T>}
        value={filterKmMax}
        onChange={(e) => setFilterKmMax(e.target.value)}
        sx={{ mb: 2 }}
      />
      <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
        <TextField
          fullWidth
          size="small"
          type="number"
          label={<T>Precio mín</T>}
          value={filterPriceMin}
          onChange={(e) => setFilterPriceMin(e.target.value)}
        />
        <TextField
          fullWidth
          size="small"
          type="number"
          label={<T>Precio máx</T>}
          value={filterPriceMax}
          onChange={(e) => setFilterPriceMax(e.target.value)}
        />
      </Box>
      <Button
        fullWidth
        size="small"
        onClick={handleClearFilters}
        sx={{ textTransform: 'none' }}
      >
        <T>Limpiar filtros</T>
      </Button>
    </>
  );

  const renderResults = () => (
    <>
      {hasActiveFilters && (
        <Typography variant="h4" align="center" sx={{ mb: 4, fontWeight: 'bold', color: 'text.primary' }}>
          <T>Resultados</T>
        </Typography>
      )}
      <Grid container>
        {loading ? (
          [1, 2, 3, 4, 5, 6].map((n) => (
            <Grid item xs={12} key={n}>
              <SkeletonCarCard />
            </Grid>
          ))
        ) : filteredCars.length > 0 ? (
          filteredCars.filter(social.isVisible).slice(0, carsLimit).map((car) => (
            <Grid item xs={12} key={car.id}>
              <ListingPost
                item={car}
                followType="car"
                social={social}
                photo={authorPhotos[car.uid]}
                featured={isFeatured(car)}
                onSimilar={(car) => setSelectedBrand(car.marca || '')}
                similarLabel={<T>Más autos similares</T>}
                media={
                  <Box sx={listingMediaSx}>
                    <ListingImageSlideshow
                      images={car.imageUrls}
                      alt={car.title}
                      height={listingImageHeight}
                      borderRadius={listingImageRadius}
                      PlaceholderIcon={DirectionsCar}
                      placeholderText={<T>Sin imágenes</T>}
                    />
                  </Box>
                }
              >
                <Typography variant={isMobile ? 'subtitle1' : 'h6'} sx={{ fontWeight: 700 }} noWrap>
                  {car.title}
                </Typography>
                <Typography variant="h6" sx={{ color: brandColors.accent, fontWeight: 800, mb: 1 }}>
                  ${car.price}
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mb: 2 }}>
                  {car.marca && <Chip size="small" icon={<DirectionsCarFilled />} label={car.marca} />}
                  {car.modelo && <Chip size="small" label={car.modelo} />}
                  {car.año && <Chip size="small" icon={<CalendarMonthIcon />} label={car.año} />}
                  {car.km && <Chip size="small" icon={<SpeedIcon />} label={<>{car.km} km</>} />}
                  {car.city && <Chip size="small" icon={<LocationOnIcon />} label={car.city} />}
                </Box>
                <Button
                  variant="contained"
                  fullWidth
                  onClick={() => navigate(`/cars/${car.id}`)}
                >
                  <T>Ver más</T>
                </Button>
              </ListingPost>
            </Grid>
          ))
        ) : (
          <Grid item xs={12}>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                textAlign: 'center',
                minHeight: '45vh',
                gap: 1.5,
                color: 'text.secondary',
              }}
            >
              <DirectionsCar sx={{ fontSize: 56, color: 'text.disabled' }} />
              <Typography sx={{ color: 'text.secondary' }}>
                <T>No se encontraron vehículos con los filtros seleccionados.</T>
              </Typography>
              <Button variant="contained" onClick={handleClearFilters}>
                <T>Limpiar búsqueda</T>
              </Button>
            </Box>
          </Grid>
        )}
      </Grid>
      {!loading && (filteredCars.length > carsLimit || !carsFeed.exhausted) && (
        <Box ref={carsSentinelRef} sx={{ height: 1 }} />
      )}
      {!loading && filteredCars.length > 0 && (
        <Typography
          variant="caption"
          align="center"
          sx={{ display: "block", color: brandColors.textSecondary, mt: 4, mb: 4 }}
        >
          © 2026 Dapur Tecnología - Notiline
        </Typography>
      )}
    </>
  );

  return (
    <Container maxWidth={false} disableGutters sx={pageContentSx}>
      {showTopBar && (
      <AppBar ref={appBarRef} position="fixed" elevation={0} sx={styles.header}>
        {!sidebarActive && (
        <Toolbar sx={styles.logoToolbar}>
          <CardMedia
            component="img"
            image={themeMode === 'dark' ? logo5 : brandColors.mode === 'light' ? logo1 : logoSvg}
            title="Logo"
            style={styles.logo}
          />
        </Toolbar>
        )}
        <CustomToolbar
          isSearchOpen={isSearchOpen}
          onToggleSearch={toggleSearch}
          onOpenFilters={() => setFiltersOpen(true)}
          showSearchFilters={!isDesktop}
        />
        {!isDesktop && isSearchOpen && (
          <Box sx={{ backgroundColor: sidebarActive ? 'transparent' : barColors.background, px: 2, py: 1 }}>
            <Box sx={{ ...pageContentSx, display: 'flex', alignItems: 'center', gap: 1 }}>
              <TextField
                fullWidth
                autoFocus
                size="small"
                placeholder="Buscar por título, marca, modelo o descripción"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                autoComplete="off"
                InputProps={{ endAdornment: <SearchClearAdornment value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />, startAdornment: <SearchIcon sx={{ mr: 1, color: brandColors.textSecondary }} /> }}
              />
            </Box>
          </Box>
        )}
      </AppBar>
      )}

      <Container maxWidth={false} disableGutters sx={{ width: '100%', mt: `${appBarHeight}px`, mb: 10 }}>
        {!isDesktop && (
          <Drawer
            anchor="bottom"
            open={filtersOpen}
            onClose={() => setFiltersOpen(false)}
            PaperProps={{ sx: { borderTopLeftRadius: 16, borderTopRightRadius: 16, maxHeight: '85vh' } }}
          >
            <Box sx={{ pt: 1.5, pb: 4, overflowY: 'auto', px: 2, maxWidth: `${PAGE_CONTENT_MAX_WIDTH}px`, mx: 'auto', boxSizing: 'border-box' }}>
              <SheetDragHandle onClose={() => setFiltersOpen(false)} />
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                <Typography variant="h6" sx={{ fontWeight: 'bold' }}><T>Filtros</T></Typography>
                <IconButton onClick={() => setFiltersOpen(false)} size="small">
                  <CloseIcon />
                </IconButton>
              </Box>
              {filtersFields}
            </Box>
          </Drawer>
        )}
        <Box
          sx={{
            display: isDesktop ? 'grid' : 'block',
            gridTemplateColumns: isDesktop ? '260px 1fr' : undefined,
            columnGap: isDesktop ? 3 : 0,
            width: '100%',
            maxWidth: isDesktop ? '1200px' : `${PAGE_CONTENT_MAX_WIDTH}px`,
            mx: 'auto',
            pt: isDesktop ? '20px' : '16px',
            px: 2,
            boxSizing: 'border-box',
          }}
        >
          {isDesktop && (
            <Paper
              variant="outlined"
              sx={{ p: 2.5, borderRadius: 3, position: 'sticky', top: appBarHeight + 16, alignSelf: 'start', maxHeight: `calc(100vh - ${appBarHeight + 16 + FILTER_PANEL_BOTTOM_GAP}px)`, ...filterPanelScrollSx }}
            >
              <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2 }}><T>Filtros</T></Typography>
              <TextField
                fullWidth
                size="small"
                placeholder="Buscar por título, marca, modelo o descripción"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                autoComplete="off"
                sx={{ mb: 3 }}
                InputProps={{ endAdornment: <SearchClearAdornment value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />, startAdornment: <SearchIcon sx={{ mr: 1, color: brandColors.textSecondary }} /> }}
              />
              {filtersFields}
            </Paper>
          )}
          <Box sx={{ minWidth: 0 }}>
            {renderResults()}
          </Box>
        </Box>
      </Container>

      <BottomToolbar />
    </Container>
  );
};

export default CarsPage;
